import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import {
	Box,
	CircularProgress,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	cancelFuturePatternChange,
	changeEditWorkerStep,
} from "../../../../export/workers";
import { SETTINGS_PERMISSIONS } from "../../../config/permissions";
import JobColor from "../../../design/ui/job-color";
import {
  deCamelCase,
  formatDate,
  hasPermissionString,
} from "../../../utils/helpers";
import useWorkersData from "../controllers/use-workers-data";

interface PrimaryJob {
	name: string;
	id: number;
	hexColor: string;
}
interface WorkerProfile {
	firstName: string;
	lastName: string;
	joiningDate: string;
	companyEmployeeId: string;
	location?: { name: string, id: number };
	mobilePhoneNumber?: string;
	managerName?: string;
	workCenter?: { name: string; id?: number };
	shiftPattern?: { name: string };
	jobTitle?: string;
	workerTypeId: string;
	primaryJob: PrimaryJob;
	workerSubTypeName?: string;
	email?: string;
	badgeNumber?: number;
	workerStatusName?: string;
	workerId?: string;
	pendingPatternChange?: {
		overrideId: number;
		shiftPatternId: number;
		shiftPatternName: string;
		effectiveDate: string;
	} | null;
}

interface WorkerType {
	id: string;
	text?: string;
	name?: string;
}

interface EditWorkerDetailsProps {
	workerProfile: WorkerProfile;
	workerTypes: WorkerType[];
}

const EditWorkerDetails: React.FC<EditWorkerDetailsProps> = ({
	workerProfile,
	workerTypes,
}) => {
	const dispatch = useDispatch();
	const confirm = useConfirm();
	const { t } = useTranslation();
	const { firstName, lastName, joiningDate, companyEmployeeId } = workerProfile;

	const { cancelFuturePatternChangeStatus } = useWorkersData();

	const { userWorkCenters } = useSelector(
		(state: any) => ({
			userWorkCenters:
				state.manageWorkers.filterData.allWorkcenters?.map((wc: any) => String(wc.id)) ||
				[],
		})
	);

	const isInUserWorkcenter = useMemo(() => {
		const workerWorkCenterId = workerProfile?.workCenter?.id;
		if (!workerWorkCenterId) return true;
		return userWorkCenters.includes(String(workerWorkCenterId));
	}, [workerProfile?.workCenter?.id, userWorkCenters]);

	const handleIsInUserWorkcenterAlert = useCallback(() => {
		confirm({
			title: "",
			description: t("workers_no-access-work-center-basic-details"),
			confirmationText: t("common_close"),
			cancellationText: "",
		});
	}, [confirm, t]);

	const handleCancelPatternChange = () => {
		if (workerProfile.workerId) {
			dispatch(cancelFuturePatternChange({ workerId: workerProfile.workerId }));
		}
	};

	const isWorkerSubTypeEnabled = useSelector(
		(state: any) => state.user.userData.workerSubTypeEnabled
	);

	const isBadgeNumberEnabled = useSelector(
		(state: any) => state.user.userData.badgeNumberEnabled
	);

	const badgeNumberEnabledLocations = useSelector(
		(state: any) => state.user.userData.badgeNumberEnabledLocations
	);

  const permissions = useSelector(
    (state: any) => state.user.userData.permissions
  );
  const canEditWorker = hasPermissionString(
    permissions,
    SETTINGS_PERMISSIONS.WORKERS.EDIT
  );

	const canShowBadgeNumber = useCallback(() => {
		if (isBadgeNumberEnabled) {
			if (badgeNumberEnabledLocations?.length > 0) {
				const locationId = workerProfile.location?.id;
				return badgeNumberEnabledLocations.includes(locationId);
			}
			return true;
		}
		return false;
	}, [
		badgeNumberEnabledLocations,
		isBadgeNumberEnabled,
		workerProfile.location?.id,
	]);

	const locationName = workerProfile.location?.name || "-";

	const phoneNumber = workerProfile.mobilePhoneNumber || "-";

	const email = workerProfile.email || "-";

	const supervisorName = workerProfile.managerName || "-";

	const workcenterName = workerProfile.workCenter?.name || "-";

	const shiftPattern = workerProfile.shiftPattern?.name || "-";

	const jobTitle = workerProfile.jobTitle || "-";

	const primaryJobName = workerProfile.primaryJob.name;

	const primaryJobColor = workerProfile.primaryJob.hexColor;

	const workerSubType = workerProfile.workerSubTypeName || "-";

	const workerStatusName = workerProfile.workerStatusName || "-";

	const badgeNumber = workerProfile.badgeNumber ?? null;

	const getWorkerType = useCallback(() => {
		const returnValue = {
			name: "-",
			text: "-",
		};
		const id = workerProfile.workerTypeId;
		if (workerTypes) {
			workerTypes.forEach((workerType) => {
				if (parseInt(workerType.id) === parseInt(id)) {
					if (workerType.text) {
						returnValue.text = workerType.text;
						returnValue.name = workerType.name || "";
					} else if (workerType.name) {
						returnValue.text = deCamelCase(workerType.name);
						returnValue.name = workerType.name;
					}
				}
			});
		}
		return returnValue;
	}, [workerProfile.workerTypeId, workerTypes]);

	return (
    <Box
      sx={{
        borderRadius: "10px",
        border: "3px solid #f4f4f4",
        //pointerEvents: 'none',
      }}
      mb={5}
    >
      <Box p={2}>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography fontWeight="bold">{t("workers_basic-details")}</Typography>

          {canEditWorker ? (
            <EditIcon
              onClick={() => {
                if (isInUserWorkcenter) {
                  dispatch(changeEditWorkerStep({ step: 1 }));
                } else {
                  handleIsInUserWorkcenterAlert();
                }
              }}
              sx={{
                cursor: "pointer",
                opacity: isInUserWorkcenter ? 1 : 0.5,
              }}
            />
          ) : (
            <Box />
          )}
        </Box>
        <Typography variant="caption">
          {/* Add in workers in personal information. */}
        </Typography>
      </Box>
      <Box pl={2} mb={1} display={"flex"}>
        <Box width={250}>
          <Typography variant="subtitle2">{t("workers_full-name")}</Typography>
        </Box>
        <Box>
          <Typography>{firstName + " " + lastName}</Typography>
        </Box>
      </Box>
      <Box pl={2} mb={1} display={"flex"}>
        <Box width={250}>
          <Typography variant="subtitle2">{t("workers_phone-number")}</Typography>
        </Box>
        <Box>
          <Typography>{phoneNumber}</Typography>
        </Box>
      </Box>
      <Box pl={2} mb={1} display={"flex"}>
        <Box width={250}>
          <Typography variant="subtitle2">{t("workers_email")}</Typography>
        </Box>
        <Box>
          <Typography>{email}</Typography>
        </Box>
      </Box>
      <Box pl={2} mb={1} display={"flex"}>
        <Box width={250}>
          <Typography variant="subtitle2">{t("workers_joining-date")}</Typography>
        </Box>
        <Box>
          {/* joining date value format : "yyyy-mm-dd" hence avoiding format function from date-fns */}
          <Typography>
            {formatDate({
              dateStr: joiningDate,
              formatType: "NUMERIC_DATE",
              ISOString: false,
            })}
          </Typography>
        </Box>
      </Box>
      {locationName !== "-" && (
        <Box pl={2} mb={1} display={"flex"}>
          <Box width={250}>
            <Typography variant="subtitle2">{t("workers_location")}</Typography>
          </Box>
          <Box>
            <Typography>{locationName}</Typography>
          </Box>
        </Box>
      )}
      <Box pl={2} mb={1} display={"flex"}>
        <Box width={250}>
          <Typography variant="subtitle2">{t("workers_work-center")}</Typography>
        </Box>
        <Box>
          <Typography>{workcenterName}</Typography>
        </Box>
      </Box>
      <Box pl={2} mb={1} display={"flex"}>
        <Box width={250}>
          <Typography variant="subtitle2">{t("workers_worker-id")}</Typography>
        </Box>
        <Box>
          <Typography>
            {companyEmployeeId && companyEmployeeId.length > 0
              ? companyEmployeeId
              : "-"}
          </Typography>
        </Box>
      </Box>
      <Box pl={2} mb={1} display={"flex"}>
        <Box width={250}>
          <Typography variant="subtitle2">{t("workers_job-title")}</Typography>
        </Box>
        <Box>
          <Typography>{jobTitle}</Typography>
        </Box>
      </Box>
      <Box pl={2} mb={1} display={"flex"}>
        <Box width={250}>
          <Typography variant="subtitle2">{t("workers_primary-job")}</Typography>
        </Box>
        {primaryJobColor && primaryJobName ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            <JobColor color={`#${primaryJobColor}`} />
            <Typography>{primaryJobName}</Typography>
          </Box>
        ) : (
          <Typography>-</Typography>
        )}
      </Box>
      <Box pl={2} mb={1} display={"flex"}>
        <Box width={250}>
          <Typography variant="subtitle2">{t("workers_worker-type")}</Typography>
        </Box>
        <Box>
          <Typography>{getWorkerType().text}</Typography>
        </Box>
      </Box>
      {workerSubType && isWorkerSubTypeEnabled && (
        <Box pl={2} mb={1} display={"flex"}>
          <Box width={250}>
            <Typography variant="subtitle2">{t("workers_worker-sub-type")}</Typography>
          </Box>
          <Box>
            <Typography>{workerSubType}</Typography>
          </Box>
        </Box>
      )}

      {workerStatusName && (
        <Box pl={2} mb={1} display={"flex"}>
          <Box width={250}>
            <Typography variant="subtitle2">{t("workers_worker-status")}</Typography>
          </Box>
          <Box>
            <Typography>{workerStatusName}</Typography>
          </Box>
        </Box>
      )}

      <Box pl={2} mb={1} display={"flex"}>
        <Box width={250}>
          <Typography variant="subtitle2">{t("workers_reporting-manager")}</Typography>
        </Box>
        <Box>
          <Typography>{supervisorName}</Typography>
        </Box>
      </Box>
      <Box pl={2} mb={1} display={"flex"}>
        <Box width={250}>
          <Typography variant="subtitle2">{t("workers_shift-pattern")}</Typography>
        </Box>
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography>{shiftPattern}</Typography>
            {workerProfile.pendingPatternChange && (
              <Box
                sx={{
                  backgroundColor: "#e3f2fd",
                  borderRadius: "4px",
                  px: 1,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  border: "1px solid #90caf9",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: "bold", color: "#1976d2" }}
                >
                  {t("workers_pending-pattern-change", {
                    name: workerProfile.pendingPatternChange.shiftPatternName,
                    date: formatDate({
                      dateStr:
                        workerProfile.pendingPatternChange.effectiveDate,
                      formatType: "NUMERIC_DATE",
                      ISOString: false,
                    }),
                  })}
                </Typography>
                {canEditWorker && (
                  <Tooltip title={t("workers_cancel-pending-change")}>
                    <IconButton
                      size="small"
                      onClick={handleCancelPatternChange}
                      disabled={cancelFuturePatternChangeStatus === "pending"}
                      sx={{ p: 0 }}
                    >
                      {cancelFuturePatternChangeStatus === "pending" ? (
                        <CircularProgress size={14} />
                      ) : (
                        <CloseIcon sx={{ fontSize: 16, color: "#d32f2f" }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {canShowBadgeNumber() && badgeNumber && (
        <Box pl={2} mb={1} display={"flex"}>
          <Box width={250}>
            <Typography variant="subtitle2">{t("workers_badge-number")}</Typography>
          </Box>
          <Box>
            <Typography>{badgeNumber}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EditWorkerDetails;
